/* =========================================================================
   MONDAIN — POSTHOG. Product analytics, session replay, heatmaps.
   Vanilla, no build step, no dependencies (the house style).

   HOW IT LOADS. snippets/mondain-posthog.liquid runs in the HEAD: it hands
   over window.MDN_POSTHOG (the key, the hosts, and everything Liquid knows
   about this page), then PostHog's own loader stub and posthog.init(). This
   file is part two — deferred, at the end of the body, after the shell and
   the Klaviyo client, so window.MDN.cart and window.MDN.klaviyo already
   exist and can be wrapped. Same ordering trick the layout already uses for
   catalogue -> shop.js -> shell.js.

   Nothing here calls init: by the time this runs, posthog is either the real
   library or the stub, and the stub queues every call until array.js lands.

   WHAT WE HAND-WIRE, AND WHAT WE DON'T. Autocapture already records every
   click, tap, form submit and input change with the element's classes and
   data-attributes, so the size chips, the nav, the filter chips, the 3D
   viewer's controls and the footer links all arrive without a line of code
   here. capture_pageleave adds time-on-page and scroll depth. This file
   only adds the four things autocapture CANNOT infer:

     1. commerce meaning  — product_viewed / add_to_cart / checkout_clicked
                            carry ids and money, not DOM labels
     2. identity          — email is the join key between the storefront,
                            the mailing list and the checkout pixel
     3. search intent     — one event per settled query, not per keystroke
     4. where time goes   — dwell per panel/section (zone_time), because a
                            MONDAIN page is one long scroll, not many URLs

   Checkout is Shopify-hosted and this file cannot reach it. The custom web
   pixel in docs/posthog/ owns that half of the funnel and stitches to the
   same person through the ph_<key>_posthog cookie.
   ========================================================================= */

(function () {
  'use strict';

  var CFG = window.MDN_POSTHOG;
  if (!CFG || !CFG.key || !window.posthog) return;

  var ctx = CFG.context || {};

  /* ------------------------------------------------------------------ */
  /* 1. The one verb the rest of the theme needs                         */
  /* ------------------------------------------------------------------ */

  function track(name, props) {
    try { posthog.capture(name, props || {}); } catch (e) {}
  }

  /* the shell's public surface gains one verb; anything in the theme can
     call MDN.track('thing_happened', {...}) without knowing about PostHog */
  window.MDN = window.MDN || {};
  window.MDN.track = track;
  window.MDN.posthog = posthog;

  /* ------------------------------------------------------------------ */
  /* 2. Who and where — stamped on every event from here on              */
  /* ------------------------------------------------------------------ */

  posthog.register({
    mdn_template: ctx.template || null,
    mdn_template_suffix: ctx.template_suffix || null,
    mdn_currency: ctx.currency || null,
    mdn_country: ctx.country || null,
    mdn_language: ctx.language || null,
    mdn_logged_in: !!ctx.logged_in
  });

  /* EMAIL IS THE JOIN KEY. A logged-in customer, a mailing-list sign-up and
     a checkout all know the address; only the storefront knows the Shopify
     customer id, and guests never have one. Identifying on the address is
     what lets the checkout pixel and this file land on one person. */
  function identify(email, props) {
    if (!email) return null;
    var id = String(email).trim().toLowerCase();
    if (!id || id.indexOf('@') === -1) return null;
    var set = props || {};
    set.email = id;
    try { posthog.identify(id, set); } catch (e) {}
    return id;
  }

  if (CFG.person && CFG.person.email) {
    identify(CFG.person.email, {
      shopify_customer_id: CFG.person.shopify_customer_id || null,
      orders_count: CFG.person.orders_count || 0,
      total_spent: CFG.person.total_spent || 0,
      customer_tags: CFG.person.tags || []
    });
  }

  /* arrived from an email link (?mdn_e=, lifted off in the head snippet) —
     the address Klaviyo sent to IS the person, logged in or not */
  if (CFG.link_email) identify(CFG.link_email, { last_email_click_at: new Date().toISOString() });

  /* ------------------------------------------------------------------ */
  /* 3. The shape of this page                                           */
  /* ------------------------------------------------------------------ */

  if (CFG.product) {
    track('product_viewed', {
      product_id: CFG.product.id,
      product_handle: CFG.product.handle,
      product_title: CFG.product.title,
      product_type: CFG.product.type,
      variant_id: CFG.product.variant_id,
      price: CFG.product.price,
      available: CFG.product.available,
      product_tags: CFG.product.tags
    });
  }

  if (CFG.collection) {
    track('collection_viewed', {
      collection_id: CFG.collection.id,
      collection_handle: CFG.collection.handle,
      collection_title: CFG.collection.title,
      products_count: CFG.collection.products_count
    });
  }

  if (CFG.search && CFG.search.terms) {
    track('search_results_viewed', {
      query: String(CFG.search.terms).toLowerCase(),
      results: CFG.search.results_count
    });
  }

  if (ctx.template === 'cart') {
    track('cart_viewed', { cart_items: ctx.cart_items, cart_value: ctx.cart_value });
  }

  /* ------------------------------------------------------------------ */
  /* 4. The bag — wrapping the shell's own verbs, the way the Meta pixel */
  /*    already wraps MDN.cart.add in shell.js                           */
  /* ------------------------------------------------------------------ */

  var lastAdd = 0;
  var cart = window.MDN.cart;

  if (cart && typeof cart.add === 'function') {
    var goldAdd = cart.add;
    cart.add = function (line) {
      lastAdd = Date.now();
      try {
        track('add_to_cart', {
          product_title: line && line.title,
          variant: line && line.variant,
          variant_id: line && line.variantId,
          price: line && line.price ? line.price / 100 : null,
          quantity: (line && line.qty) || 1,
          from_template: ctx.template
        });
      } catch (e) {}
      return goldAdd.apply(this, arguments);
    };
  }

  if (cart && typeof cart.open === 'function') {
    var goldOpen = cart.open;
    cart.open = function () {
      /* an add opens the bag itself — that is one intent, not two */
      if (Date.now() - lastAdd > 1200) track('bag_opened', { cart_items: ctx.cart_items });
      return goldOpen.apply(this, arguments);
    };
  }

  /* The LAST thing we can see before Shopify takes over. Shopify's own
     checkout_started (from the web pixel) fires on the far side; this one
     is the click, so the gap between them is the drop-off worth watching. */
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest
      ? e.target.closest('.mdn-bag__checkout, [name="checkout"], a[href*="/checkout"]')
      : null;
    if (!t) return;
    track('checkout_clicked', {
      from_template: ctx.template,
      cart_items: ctx.cart_items,
      cart_value: ctx.cart_value
    });
  }, true);

  /* ------------------------------------------------------------------ */
  /* 5. The mailing list — an address here names the person for good      */
  /* ------------------------------------------------------------------ */

  var kl = window.MDN.klaviyo;
  if (kl && typeof kl.collect === 'function') {
    var goldCollect = kl.collect;
    kl.collect = function (email, props, source) {
      try {
        identify(email, { newsletter_source: source || null });
        track('email_captured', {
          source: source || null,
          from_template: ctx.template
        });
      } catch (e) {}
      return goldCollect.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------ */
  /* 6. Search — one event per settled query                             */
  /* ------------------------------------------------------------------ */

  var field = document.querySelector('.mdn-search__input');
  if (field) {
    var timer = null;
    var last = '';
    field.addEventListener('input', function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        var q = field.value.trim();
        if (q.length < 2 || q.toLowerCase() === last) return;
        last = q.toLowerCase();
        track('search_performed', {
          query: last,
          /* shop.js has already re-rendered the collection behind the glass */
          results: document.querySelectorAll('.mdn-shop__card').length
        });
      }, 900);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 7. WHERE THE TIME GOES                                              */
  /*                                                                     */
  /*    A MONDAIN page is one long scroll, so "which page" answers almost */
  /*    nothing — the question is which PANEL held them. Every            */
  /*    [data-mdn-panel] (DESIGN's four, and any panel added later),      */
  /*    every .shopify-section, and anything marked [data-ph-zone] is a   */
  /*    zone. A zone is "being read" while it crosses the middle band of  */
  /*    the viewport, which is height-agnostic — a panel taller than the  */
  /*    screen still counts, and only one zone counts at a time.          */
  /*                                                                     */
  /*    Time is banked, not streamed: zone_time carries the seconds since */
  /*    the last report, flushed when the tab is hidden or the page goes  */
  /*    away. Sum the property in PostHog for the true total.             */
  /*                                                                     */
  /*    Opt a zone out with data-ph-zone-skip; name one with              */
  /*    data-ph-zone="the name you want to read in PostHog".              */
  /* ------------------------------------------------------------------ */

  var zones = [];

  function zoneName(el) {
    var explicit = el.getAttribute('data-ph-zone');
    if (explicit) return explicit;
    var label = el.getAttribute('aria-label');
    if (label) return label;
    if (el.id) return el.id.replace(/^shopify-section-/, '');
    var cls = String(el.className || '').split(' ')[0];
    return cls || el.tagName.toLowerCase();
  }

  function buildZones() {
    var found = document.querySelectorAll('[data-ph-zone], [data-mdn-panel], .shopify-section');
    var out = [];
    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      if (el.hasAttribute('data-ph-zone-skip')) continue;
      /* finest grain wins: a section that merely CONTAINS a panel is not
         itself a zone, or its time would double-count the panel's */
      var wraps = false;
      for (var j = 0; j < found.length; j++) {
        if (found[j] !== el && el.contains(found[j])) { wraps = true; break; }
      }
      if (wraps) continue;
      out.push({ el: el, name: zoneName(el), ms: 0, since: 0, visible: false });
    }
    return out;
  }

  function bank(z) {
    if (!z.since) return;
    z.ms += Date.now() - z.since;
    z.since = 0;
  }

  function flush(reason) {
    for (var i = 0; i < zones.length; i++) {
      var z = zones[i];
      bank(z);
      if (z.ms < 2000) continue;   /* a glance is not a read */
      track('zone_time', {
        zone: z.name,
        seconds: Math.round(z.ms / 1000),
        from_template: ctx.template,
        reason: reason
      });
      z.ms = 0;
    }
  }

  if (window.IntersectionObserver) {
    zones = buildZones();
  }

  if (zones.length) {
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        for (var j = 0; j < zones.length; j++) {
          if (zones[j].el !== entry.target) continue;
          zones[j].visible = entry.isIntersecting;
          if (entry.isIntersecting) {
            if (!zones[j].since) zones[j].since = Date.now();
          } else {
            bank(zones[j]);
          }
          break;
        }
      }
    }, {
      /* the middle 20% of the screen is "where you are looking" */
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0
    });

    for (var z = 0; z < zones.length; z++) io.observe(zones[z].el);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        flush('hidden');
        return;
      }
      /* back from a background tab: restart the clock where they left it */
      var now = Date.now();
      for (var i = 0; i < zones.length; i++) {
        if (zones[i].visible) zones[i].since = now;
      }
    });

    window.addEventListener('pagehide', function () { flush('pagehide'); });
  }
})();
